(() => {
  if (window.yndStoreInitialized) return;
  window.yndStoreInitialized = true;
  const variantRequests = new WeakMap();
  const imageTriggers = new WeakMap();
  const touchStarts = new WeakMap();
  const swipeTimes = new WeakMap();

  const galleryObservers = new WeakMap();
  const pauseGalleryVideos = (gallery) => gallery?.querySelectorAll('video').forEach(video => video.pause());
  const destroyGallery = (gallery) => {
    if (!gallery) return;
    pauseGalleryVideos(gallery);
    galleryObservers.get(gallery)?.disconnect();
    galleryObservers.delete(gallery);
    touchStarts.delete(gallery);
    swipeTimes.delete(gallery);
    gallery.querySelector('[data-image-dialog][open]')?.close();
    delete gallery.dataset.galleryReady;
  };
  const gallerySignature = gallery => [...gallery.querySelectorAll('[data-gallery-slide]')].map(slide => `${slide.dataset.mediaType}:${slide.dataset.mediaId}`).join(',');

  const updateGallery = (gallery, index) => {
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    if (!slides.length) return;
    const nextIndex = (index + slides.length) % slides.length;
    const changed = gallery.dataset.galleryIndex !== undefined && Number(gallery.dataset.galleryIndex) !== nextIndex;
    slides.forEach((slide, slideIndex) => {
      slide.dataset.active = String(slideIndex === nextIndex);
      if (slideIndex !== nextIndex && window.matchMedia('(max-width: 800px)').matches) pauseGalleryVideos(slide);
    });
    gallery.dataset.galleryIndex = String(nextIndex);
    if (changed && window.matchMedia('(max-width: 800px)').matches) slides[nextIndex].dispatchEvent(new Event('ynd:content-change', {bubbles:true}));
    const counter = gallery.querySelector('[data-gallery-count]');
    if (counter) counter.textContent = `${nextIndex + 1} / ${slides.length}`;
    const progress = gallery.querySelector('[data-gallery-progress]');
    if (progress) {
      progress.style.setProperty('--gallery-progress', `${100 / slides.length}%`);
      progress.style.setProperty('--gallery-offset', `${nextIndex * 100}%`);
    }
  };

  const initializeGallery = (gallery) => {
    if (!gallery || gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';
    gallery.querySelectorAll('[data-gallery-controls], [data-gallery-progress]').forEach(element => { element.hidden = false; });
    updateGallery(gallery, 0);
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
      }, { threshold: 0 });
      gallery.querySelectorAll('video').forEach(video => observer.observe(video));
      galleryObservers.set(gallery, observer);
    }
    const dialog = gallery.querySelector('[data-image-dialog]');
    dialog?.addEventListener('close', () => {
      document.dispatchEvent(new Event('ynd:overlay'));
      const trigger = imageTriggers.get(dialog);
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      imageTriggers.delete(dialog);
    });
  };

  const sizeKey = (value) => {
    const key = String(value || '').toLowerCase().replace(/[\s_-]/g, '');
    return ({xs:'xs',xsmall:'xs',s:'s',small:'s',m:'m',medium:'m',l:'l',large:'l',xl:'xl',xlarge:'xl',xxl:'xxl','2xl':'xxl',xxlarge:'xxl','2xlarge':'xxl',xxxl:'3xl','3xl':'3xl',xxxlarge:'3xl','3xlarge':'3xl'})[key] || '';
  };

  const updateProductHelp = (section) => {
    const size = section.querySelector('.ynd-product-option--size [data-option-input]:checked')?.value || '';
    const color = section.querySelector('.ynd-product-option--color [data-option-input]:checked')?.value || '';
    const key = sizeKey(size);
    const guide = section.querySelector('[data-size-guide]');
    if (guide) {
      const selected = guide.querySelector('[data-guide-selected]');
      selected.hidden = !key;
      selected.textContent = key ? `${selected.dataset.selectedLabel} ${size}` : '';
      guide.querySelectorAll('[data-size-key]').forEach(row => {
        const matches = Boolean(key) && row.dataset.sizeKey === key;
        row.classList.toggle('is-selected', matches);
        row.querySelector('[data-row-selected]').hidden = !matches;
      });
    }
    section.querySelectorAll('[data-product-inquiry]').forEach(link => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('product', section.dataset.productTitle || '');
      url.searchParams.set('product_url', section.dataset.productUrl || '');
      url.searchParams.set('color', color);
      url.searchParams.set('size', size);
      url.searchParams.set('topic', link.dataset.inquiryTopic || 'style');
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  };

  const initializeInquiry = (target) => {
    const textarea = target.querySelector('[data-inquiry-message]');
    if (!textarea || textarea.dataset.inquiryReady === 'true') return;
    textarea.dataset.inquiryReady = 'true';
    if (textarea.value.trim()) return;
    const params = new URLSearchParams(window.location.search);
    const clean = (name, limit) => (params.get(name) || '').replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, limit).trim();
    const product = clean('product', 240);
    const path = clean('product_url', 500);
    if (!product || !path.startsWith('/') || path.startsWith('//')) return;
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return;
    const color = clean('color', 100);
    const size = clean('size', 60);
    const labels = textarea.dataset;
    const lines = [params.get('topic') === 'size' ? labels.sizeIntro : labels.styleIntro, '', `${labels.productLabel}: ${product}`];
    if (color) lines.push(`${labels.colorLabel}: ${color}`);
    if (size) lines.push(`${labels.sizeLabel}: ${size}`);
    lines.push(`${labels.linkLabel}: ${url.origin}${url.pathname}`, '', labels.questionLabel, '');
    textarea.value = lines.join('\n');
    textarea.closest('form')?.querySelector('[data-inquiry-context]')?.removeAttribute('hidden');
  };

  const initializeProduct = (section) => {
    const pickers = section.querySelector('[data-option-pickers]');
    if (pickers) pickers.hidden = false;
    initializeGallery(section.querySelector('[data-product-gallery]'));
    updateProductHelp(section);
  };

  const sectionsWithin = (target) => {
    const sections = [...target.querySelectorAll('[data-product-section]')];
    if (target.matches?.('[data-product-section]')) sections.unshift(target);
    return sections;
  };

  sectionsWithin(document).forEach(initializeProduct);
  initializeInquiry(document);
  document.addEventListener('shopify:section:load', event => initializeInquiry(event.target));
  document.addEventListener('shopify:section:load', event => sectionsWithin(event.target).forEach(initializeProduct));
  document.addEventListener('shopify:section:unload', event => {
    sectionsWithin(event.target).forEach(section => {
      variantRequests.get(section)?.abort();
      variantRequests.delete(section);
      destroyGallery(section.querySelector('[data-product-gallery]'));
    });
  });

  document.addEventListener('change', async (event) => {
    if (!event.target.matches('[data-option-input], select[data-option-position]')) return;
    const picker = event.target;
    const section = picker.closest('[data-product-section]');
    if (!section) return;
    const chosenOption = picker.matches('select') ? picker.selectedOptions[0] : picker;
    if (!chosenOption) return;
    const selectedInputs = [...section.querySelectorAll('[data-option-input]:checked, select[data-option-position]')];
    const optionValues = selectedInputs.map(input => input.matches('select') ? input.selectedOptions[0]?.dataset.optionValueId : input.dataset.optionValueId).filter(Boolean);
    const productUrl = chosenOption.dataset.productUrl || section.dataset.productUrl;
    const destination = new URL(productUrl, window.location.origin);
    destination.searchParams.set('option_values', optionValues.join(','));
    const currentProductUrl = new URL(section.dataset.productUrl, window.location.origin);
    if (destination.origin !== currentProductUrl.origin || destination.pathname !== currentProductUrl.pathname) {
      window.location.assign(destination.href);
      return;
    }
    const selectedLabel = picker.closest('[data-option-group]')?.querySelector('[data-selected-option]');
    if (selectedLabel) selectedLabel.textContent = picker.value;
    updateProductHelp(section);
    const requestUrl = new URL(destination);
    requestUrl.searchParams.set('section_id', section.dataset.sectionId);
    const variantInput = section.querySelector('[data-variant-id]');
    const button = section.querySelector('[data-add-to-bag]');
    if (!variantInput || !button) return;
    variantRequests.get(section)?.abort();
    const controller = new AbortController();
    variantRequests.set(section, controller);
    const previousQuantity = section.querySelector('[data-product-quantity]')?.value;
    button.disabled = true;
    variantInput.disabled = true;
    section.dataset.variantLoading = 'true';
    section.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(requestUrl.href, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Product options could not be loaded');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextSection = [...html.querySelectorAll('[data-product-section]')].find(element => element.dataset.sectionId === section.dataset.sectionId);
      const nextInput = nextSection?.querySelector('[data-variant-id]');
      const nextPickers = nextSection?.querySelector('[data-option-pickers]');
      const nextPurchase = nextSection?.querySelector('[data-product-purchase]');
      if (!nextSection || !nextInput || !nextPickers || !nextPurchase) throw new Error('Product options were not returned');
      if (variantRequests.get(section) !== controller || !section.isConnected) return;
      nextPickers.hidden = false;
      section.querySelector('[data-option-pickers]').replaceWith(nextPickers);
      ['data-product-price-state', 'data-product-photo-state', 'data-product-structured-data'].forEach(attribute => {
        const currentElement = section.querySelector(`[${attribute}]`);
        const nextElement = nextSection.querySelector(`[${attribute}]`);
        if (currentElement && nextElement) currentElement.replaceWith(nextElement);
      });
      section.querySelector('[data-product-purchase]').replaceWith(nextPurchase);
      updateProductHelp(section);
      const quantity = section.querySelector('[data-product-quantity]');
      if (quantity && previousQuantity) {
        const minimum = Number(quantity.min) || 1;
        const increment = Number(quantity.step) || 1;
        const maximum = Number(quantity.max) || Infinity;
        const requestedQuantity = Number(previousQuantity);
        if (Number.isFinite(requestedQuantity)) quantity.value = String(minimum + Math.max(0, Math.floor((Math.min(maximum, requestedQuantity) - minimum) / increment)) * increment);
      }
      const gallery = section.querySelector('[data-product-gallery]');
      const nextGallery = nextSection.querySelector('[data-product-gallery]');
      if (gallery && nextGallery && gallerySignature(gallery) !== gallerySignature(nextGallery)) {
        destroyGallery(gallery);
        gallery.replaceWith(nextGallery);
        initializeGallery(nextGallery);
        nextGallery.querySelector('.ynd-gallery-images')?.dispatchEvent(new Event('ynd:content-change', {bubbles:true}));
      }
      variantInput.value = nextInput.value;
      const pending = nextSection.dataset.salePending === 'true';
      const available = nextSection.dataset.variantAvailable === 'true' && !pending && !nextInput.disabled && Boolean(nextInput.value);
      variantInput.disabled = !available;
      section.dataset.selectedVariantId = nextInput.value;
      section.dataset.variantAvailable = String(available);
      section.dataset.salePending = String(pending);
      section.dataset.variantCount = nextSection.dataset.variantCount;
      const nextButton = section.querySelector('[data-add-to-bag]');
      nextButton.disabled = !available;
      nextButton.textContent = available ? nextButton.dataset.availableLabel : nextButton.dataset.unavailableLabel;
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.delete('section_id');
      pageUrl.searchParams.delete('variant');
      pageUrl.searchParams.delete('option_values');
      if (nextInput.value) pageUrl.searchParams.set('variant', nextInput.value);
      else pageUrl.searchParams.set('option_values', optionValues.join(','));
      history.replaceState(history.state, '', pageUrl);
      const focusTarget = [...section.querySelectorAll('[data-option-input]')].find(input => input.dataset.optionValueId === chosenOption.dataset.optionValueId);
      focusTarget?.focus({ preventScroll: true });
      section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variantId: nextInput.value || null, available, salePending: pending } }));
    } catch (error) {
      if (error.name !== 'AbortError' && variantRequests.get(section) === controller && section.isConnected) window.location.assign(destination.href);
    } finally {
      if (variantRequests.get(section) === controller) {
        variantRequests.delete(section);
        delete section.dataset.variantLoading;
        section.removeAttribute('aria-busy');
      }
    }
  });

  document.addEventListener('submit', (event) => {
    const section = event.target.closest('[data-product-section]');
    const variantInput = event.target.querySelector('[data-variant-id]');
    if (!section || !variantInput) return;
    if (section.dataset.variantLoading === 'true' || section.dataset.variantAvailable !== 'true' || section.dataset.salePending === 'true' || variantInput.disabled || !variantInput.value) event.preventDefault();
  });

  document.addEventListener('click', (event) => {
    const gallery = event.target.closest('[data-product-gallery]');
    if (gallery) {
      const swipeTime = swipeTimes.get(gallery);
      if (swipeTime && performance.now() - swipeTime < 500) {
        swipeTimes.delete(gallery);
        event.preventDefault();
        return;
      }
      const previous = event.target.closest('[data-gallery-previous]');
      const next = event.target.closest('[data-gallery-next]');
      if (previous || next) updateGallery(gallery, Number(gallery.dataset.galleryIndex || 0) + (next ? 1 : -1));
      const imageLink = event.target.closest('[data-image-open]');
      const dialog = gallery.querySelector('[data-image-dialog]');
      if (imageLink && dialog && typeof dialog.showModal === 'function' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        const image = dialog.querySelector('[data-dialog-image]');
        const sourceImage = imageLink.querySelector('img');
        image.src = imageLink.href;
        image.alt = sourceImage?.alt || '';
        image.width = sourceImage?.naturalWidth || sourceImage?.width || 1200;
        image.height = sourceImage?.naturalHeight || sourceImage?.height || 1500;
        pauseGalleryVideos(gallery);
        imageTriggers.set(dialog, imageLink);
        if (!dialog.open) dialog.showModal();
        document.dispatchEvent(new Event('ynd:overlay'));
        dialog.querySelector('[data-image-close]')?.focus();
      }
      if (event.target.closest('[data-image-close]')) dialog?.close();
      if (dialog && event.target === dialog) dialog.close();
    }
    const fitLink = event.target.closest('[data-fit-guide]');
    if (fitLink) {
      const guide = document.getElementById(fitLink.getAttribute('href').slice(1));
      if (guide) {
        event.preventDefault();
        guide.open = true;
        guide.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        guide.querySelector('summary')?.focus({ preventScroll: true });
      }
    }
  });

  document.addEventListener('pointerdown', event => {
    if (event.pointerType !== 'touch' || event.target.closest('video')) return;
    const gallery = event.target.closest('[data-product-gallery]');
    if (gallery && !gallery.querySelector('[data-image-dialog][open]')) touchStarts.set(gallery, { x: event.clientX, y: event.clientY });
  }, { passive: true });
  document.addEventListener('pointerup', event => {
    const gallery = event.target.closest('[data-product-gallery]');
    const start = gallery && touchStarts.get(gallery);
    if (!start) return;
    touchStarts.delete(gallery);
    const distance = event.clientX - start.x;
    if (Math.abs(distance) > 50 && Math.abs(distance) > Math.abs(event.clientY - start.y) * 1.5 && window.matchMedia('(max-width: 800px)').matches) {
      swipeTimes.set(gallery, performance.now());
      updateGallery(gallery, Number(gallery.dataset.galleryIndex || 0) + (distance < 0 ? 1 : -1));
    }
  }, { passive: true });
  document.addEventListener('pointercancel', event => {
    const gallery = event.target.closest('[data-product-gallery]');
    if (gallery) touchStarts.delete(gallery);
  }, { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('[data-product-gallery]').forEach(pauseGalleryVideos);
  });
  window.addEventListener('pagehide', () => document.querySelectorAll('[data-product-gallery]').forEach(pauseGalleryVideos));
  window.matchMedia('(max-width: 800px)').addEventListener('change', () => {
    document.querySelectorAll('[data-product-gallery]').forEach(gallery => updateGallery(gallery, Number(gallery.dataset.galleryIndex || 0)));
  });

  document.addEventListener('keydown', event => {
    if (event.target.closest('video, input, select, textarea')) return;
    const gallery = event.target.closest('[data-product-gallery]');
    if (gallery && !gallery.querySelector('[data-image-dialog][open]') && window.matchMedia('(max-width: 800px)').matches && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      updateGallery(gallery, Number(gallery.dataset.galleryIndex || 0) + (event.key === 'ArrowRight' ? 1 : -1));
    }
  });
})();
